import React, { forwardRef, useImperativeHandle } from "react";
import { FolderOpen } from "lucide-react";
import useProjectFiles from "../hooks/useProjectFiles";
import FileUpload from "./FileUpload";
import { useReadOnlyMode } from "../hooks/useReadOnlyMode";
import { formatFileSize } from "./code-editor-panel/utils/fileUtils";

const ProjectFilesViewer = forwardRef(({ workspaceId }, ref) => {
  const {
    files: projectFiles,
    loading: filesLoading,
    error: filesError,
    uploadFile,
    uploadMultipleFiles,
    deleteFile,
    downloadFile,
    clearError: clearFilesError,
    getFiles, // if exposed by the hook; otherwise refresh will be a no-op
  } = useProjectFiles(workspaceId);

  const { isReadOnly } = useReadOnlyMode();

  useImperativeHandle(ref, () => ({
    refresh: () => {
      if (typeof getFiles === "function") {
        getFiles();
      }
    },
  }));

  return (
    <div className="h-full bg-white dark:bg-neutral-800 overflow-hidden flex flex-col">
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
        <h3 className="text-sm font-medium text-ui-text-primary flex items-center">
          <FolderOpen size={16} className="mr-2" />
          Project Files ({projectFiles?.length || 0})
        </h3>
        {projectFiles?.length > 0 && (
          <span className="text-xs text-ui-text-secondary">
            Total: {formatFileSize(projectFiles.reduce((sum, file) => sum + file.file_size, 0))}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-hidden p-4">
        <FileUpload
          onFileUpload={uploadFile}
          onMultipleFileUpload={uploadMultipleFiles}
          files={projectFiles}
          onFileDelete={isReadOnly ? () => {} : deleteFile}
          onFileDownload={downloadFile}
          loading={filesLoading}
          error={filesError}
          onClearError={clearFilesError}
          readOnly={isReadOnly}
        />
      </div>
    </div>
  );
});

export default ProjectFilesViewer;
